import React from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay, EffectCoverflow } from 'swiper/modules';
import { motion } from 'framer-motion';
import type { FeaturedCampaign, CarouselConfig } from './types';

/**
 * FeaturedCampaignCarousel Component
 * 
 * A highly reusable carousel component for displaying featured campaigns
 * with smooth animations and glassmorphism effects
 * 
 * @param campaigns - Array of campaign data to display
 * @param config - Optional carousel configuration
 * 
 * @example
 * ```tsx
 * <FeaturedCampaignCarousel 
 *   campaigns={campaignsData}
 *   config={{ autoplay: true, loop: true }}
 * />
 * ```
 */

interface FeaturedCampaignCarouselProps {
  campaigns: FeaturedCampaign[];
  config?: CarouselConfig;
  className?: string;
}

const FeaturedCampaignCarousel: React.FC<FeaturedCampaignCarouselProps> = ({ 
  campaigns,
  config = {},
  className = ''
}) => {
  // Default configuration
  const defaultConfig: CarouselConfig = {
    autoplay: true,
    autoplayDelay: 5000,
    loop: true,
    speed: 800,
    spaceBetween: 30,
    effect: 'coverflow',
    navigation: true,
    pagination: true,
  };

  // Merge default config with user config
  const carouselConfig = { ...defaultConfig, ...config };

  return (
    <div className={`featured-campaign-carousel ${className}`}>
      <Swiper
        modules={[Navigation, Pagination, Autoplay, EffectCoverflow]}
        spaceBetween={carouselConfig.spaceBetween}
        slidesPerView={1}
        speed={carouselConfig.speed}
        loop={carouselConfig.loop}
        autoplay={
          carouselConfig.autoplay
            ? {
                delay: carouselConfig.autoplayDelay,
                disableOnInteraction: false,
                pauseOnMouseEnter: true,
              }
            : false
        }
        effect={carouselConfig.effect}
        coverflowEffect={{
          rotate: 0,
          stretch: 0,
          depth: 100,
          modifier: 2,
          slideShadows: false,
        }}
        navigation={carouselConfig.navigation}
        pagination={
          carouselConfig.pagination
            ? {
                clickable: true,
                dynamicBullets: true,
              }
            : false
        }
        className="h-full"
      >
        {campaigns.map((campaign) => (
          <SwiperSlide key={campaign.id}>
            <CampaignCard campaign={campaign} />
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Custom Styles */}
      <style>{`
        .featured-campaign-carousel .swiper {
          width: 100%;
          height: 100%;
          padding: 0;
        }

        .featured-campaign-carousel .swiper-slide {
          display: flex;
          justify-content: center;
          align-items: center;
        }

        /* Custom Navigation Buttons */
        .featured-campaign-carousel .swiper-button-next,
        .featured-campaign-carousel .swiper-button-prev {
          color: #f97316;
          background: rgba(33, 33, 33, 0.8);
          backdrop-filter: blur(1px);
          width: 48px;
          height: 48px;
          border-radius: 50%;
          transition: all 0.3s ease;
        }

        .featured-campaign-carousel .swiper-button-next:hover,
        .featured-campaign-carousel .swiper-button-prev:hover {
          background: rgba(249, 115, 22, 0.9);
          color: white;
          transform: scale(1.1);
        }

        .featured-campaign-carousel .swiper-button-next:after,
        .featured-campaign-carousel .swiper-button-prev:after {
          font-size: 20px;
          font-weight: bold;
        }

        /* Custom Pagination */
        .featured-campaign-carousel .swiper-pagination-bullet {
          background: rgba(249, 115, 22, 0.5);
          opacity: 0.5;
          width: 10px;
          height: 10px;
          transition: all 0.3s ease;
        }

        .featured-campaign-carousel .swiper-pagination-bullet-active {
          background: #f97316;
          opacity: 1;
          width: 30px;
          border-radius: 5px;
        }

        /* Smooth transitions */
        .featured-campaign-carousel .swiper-slide-active {
          z-index: 2;
        }
      `}</style>
    </div>
  );
};

/**
 * Individual Campaign Card Component
 */
interface CampaignCardProps {
  campaign: FeaturedCampaign;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign }) => {
  return (
    <div className="rounded-xl relative overflow-hidden h-[340px] w-full group">
      {/* Background Image - Full */}
      <div 
        className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-105"
        style={{
          backgroundImage: `url(${campaign.image})`,
          backgroundPosition: "top"
        }}
      />

      {/* Bottom Bar with Blur - Title Left, Button Right */}
      <div className="absolute bottom-0 left-0 right-0 p-2 flex items-center justify-between"
        style={{
          backdropFilter: 'blur(10px)',
          background: 'rgba(33, 33, 33, 0.7)',
          border: '1px solid rgba(224, 224, 224, 0.1)'
        }}
      >
        {/* Title - Left */}
        <motion.h3 
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-lg font-medium text-white flex-1"
        >
          {campaign.title}
        </motion.h3>

        {/* Action Button - Right */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {campaign.buttonPrimary && (
            <button 
              onClick={campaign.buttonPrimary.onClick}
              className="px-6 py-2.5 cursor-pointer bg-[#EE4423] hover:bg-[#d63d20] text-white rounded-full transition-all font-normal text-sm whitespace-nowrap"
            >
              {campaign.buttonPrimary.text}
            </button>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default FeaturedCampaignCarousel;
